import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { addDaySuffix } from "@ecologi/shared";
import { getLocationPlans, User } from "@ecologi/shared";
import { useStore } from "../../../store";
import {
  APIError,
  updateSubscription,
  formatAPIErrorForView,
} from "../../../lib";
import { selectSubscriptionAddons } from "../selectors";

export function useUpdateSubscription() {
  const { user, location, setUser } = useStore();

  return useMutation(
    async ({
      addons: selectedAddons,
      shortCode: selectedShortCode,
      localProjectAddon,
      numLocalTrees,
    }: any) => {
      const isBusinessPlan = selectedShortCode.includes("business");
      const isFamilyPlan = selectedShortCode.includes("family");

      if (!selectedShortCode) {
        throw new Error("No plan found. Please make sure a plan is selected.");
      }

      if (!user || !user.subscription) {
        throw new Error("You are not authorized to update subscription.");
      }

      if (!user.hasCard()) {
        throw new Error(
          "No saved card. Please add a card to your account before changing your subscription."
        );
      }

      const { familyAddonsForLocation, businessAddonsForLocation } =
        getLocationPlans(location);

      const addons = selectSubscriptionAddons(
        selectedShortCode,
        isBusinessPlan ? businessAddonsForLocation : familyAddonsForLocation,
        localProjectAddon,
        selectedAddons,
        numLocalTrees
      );

      const isAddonBasedPlan =
        isBusinessPlan || isFamilyPlan || numLocalTrees > 0;

      return updateSubscription(user.subscription["processorId"], {
        shortCode: selectedShortCode,
        addons: isAddonBasedPlan
          ? addons.map(({ id, quantity }) => ({ shortCode: id, quantity }))
          : [],
      });
    },
    {
      onSuccess: (user) => {
        const newUser = new User(user);
        const dayWithSuffix = addDaySuffix(
          DateTime.fromJSDate(newUser.subscription.nextBillingAt).toFormat("d")
        );
        const [month, year] = DateTime.fromJSDate(
          newUser.subscription.nextBillingAt
        )
          .toFormat(`MMM kkkk`)
          .split(" ");

        setUser(newUser);
        toast.success(
          `Your monthly subscription has been updated. The changes will be reflected on ${dayWithSuffix} ${month}, ${year}`
        );
      },
      onError: (error: Error | APIError) => {
        toast.error(
          error instanceof APIError
            ? formatAPIErrorForView(error)
            : error.message
        );
      },
    }
  );
}
